import memoize from 'memoizee';
import { contract } from '../../contract';
import { sleepUntil } from '@/utils/Async';
import { multiCall } from '@/utils/Calls';
import config from '@/constants/config';
import FACTORY_GAUGE_ABI from '@/constants/abis/json/mim/gauge';
import { chunkArr, arrayToHashmap } from '../../utils/helpers';
import getPoolUsdFigure from './getPoolUsdFigure';

const getFactoryOnlyPoolsBalances = memoize(async (userAddress, factoryOnlyPools) => {
    const hasFactoryRegistry = !!config.factoryV2RegistryAddress;
    const poolIds = factoryOnlyPools.map(({ id }) => id);
    await sleepUntil(() => contract.multicall);

    if(!userAddress || !hasFactoryRegistry) return { balances: [], sumBalances: 0 };

    // { compound: -1, … }
    const initialBalancesValues = arrayToHashmap(factoryOnlyPools.map(({ id }) => [id, -1]));

    contract.contracts.compound = contract;
    let calls = factoryOnlyPools.flatMap(({ address, lpTokenAddress }) => [
        // balanceOf(address)
        // Facto V2 pools have the swap and lp token combined (so address === lpTokenAddress), while
        // facto crypto pools have a separate lpTokenAddress
        [lpTokenAddress || address, '0x70a08231000000000000000000000000' + userAddress.slice(2)],
        // get_virtual_price
        // [address, "0xbb7b8b80"],
    ]);


    let aggcalls = await contract.multicall.methods.aggregate(calls).call()
    let decoded = aggcalls[1].map((hex) => contract.web3.eth.abi.decodeParameter('uint256', hex))

    const balances = initialBalancesValues;

    let i = 0;
    // const values = chunkArr(decoded, 2).slice(0, poolIds.length);
    const values = chunkArr(decoded, 1).slice(0, poolIds.length);
    for (const v of values) {
        const key = poolIds[i];
        const pool = factoryOnlyPools[i];
        // const poolBalance = +v[0] * (+v[1]) / 1e36;
        const poolBalance = +v[0] / 1e18; // Not using virtual price because it's reverting in multicall, assumes virtual price close to 1 which will def not always be the case
        balances[key] = await getPoolUsdFigure(poolBalance, pool);
        i += 1;
    }

    const factoPoolsWithGauges = factoryOnlyPools.filter(({ gaugeAddress, gauge }) => gaugeAddress || gauge);
    const stakedBalances = await multiCall(factoPoolsWithGauges.map((pool) => ({
        address: pool.gaugeAddress || pool.gauge,
        abi: FACTORY_GAUGE_ABI,
        methodName: 'balanceOf',
        params: [userAddress],
        metaData: { pool },
    })));

    for (const stakedBalanceReturn of stakedBalances) {
        const { data, metaData } = stakedBalanceReturn;
        const stakedBalance = data / 1e18;
        const { pool } = metaData;

        balances[pool.id] += await getPoolUsdFigure(stakedBalance, pool);
    }

    return {
        balances,
        sumBalances: Object.values(balances || {}).reduce((a, b) => a + b, 0),
    };
}, {
    promise: true,
    maxAge: 10 * 1000, // 10s
});

export default getFactoryOnlyPoolsBalances;
